/* eslint-disable react/jsx-no-useless-fragment */
import type { SelectProps } from '@mindpal-co/mindpal-ui';
import { Select as BaseSelect } from '@mindpal-co/mindpal-ui';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';

import type { LabelStyles } from './Form/Fields/Label';
import Label, { labelStyles } from './Form/Fields/Label';
import Text from './Text';

export type ControlStyleProps = VariantProps<ReturnType<typeof controlStyle>>;
const controlStyle = ({
  isFocused,
  isDisabled,
  error,
}: {
  isFocused: boolean;
  isDisabled: boolean;
  error: boolean;
}) =>
  cva(
    classNames('flex-1  p-2', {
      '!bg-neutral-900 border border-neutral-700': isDisabled && !error,
    }),
    {
      variants: {
        variant: {
          primary: classNames('rounded-lg text-white border-2', {
            '!border-primary-500': isFocused,
            'border-error bg-error-20%': error,
            'border-transparent bg-primary-900': !error,
          }),
          'candidate-primary': classNames(
            '!bg-jb-neutral-100 rounded-lg text-jb-neutral-1000 border hover:cursor-pointer',
            {
              '!border-jb-primary-500': isFocused,
              'border-error bg-error-20%': error,
              'border-neutral-500 bg-secondary-500/20': !error,
            }
          ),
          'neutral-1000': classNames('rounded-lg text-white  border-2', {
            '!border-neutral-500': isFocused,
            'border-error bg-error-20%': error,
            'border-transparent bg-neutral-1000': !error,
          }),
          gray: classNames('rounded-lg text-white border', {
            '!border-neutral-500': isFocused,
            'border-error bg-error-20%': error,
            'border-neutral-600 bg-neutral-1000': !error,
          }),
          grayWhiteText: classNames('rounded-lg text-white border', {
            '!border-neutral-500': isFocused,
            'border-error bg-error-20%': error,
            'border-neutral-600 bg-neutral-1000': !error,
          }),
          lightGray: classNames('rounded-lg text-neutral-1000 border', {
            '!border-secondary-700': isFocused,
            'border-error bg-error-20%': error,
            'border-neutral-300': !error,
          }),
          secondary: classNames('rounded-lg text-white  border-2', {
            '!border-secondary-500': isFocused,
            'border-error bg-error-20%': error,
            'border-transparent bg-secondary-500/20': !error,
          }),
          light: classNames(
            '!bg-neutral-100 rounded-lg text-neutral-1000 border !border-neutral-1000',
            {
              '!border-primary-500': isFocused,
              'border-error bg-error-20%': error,
              'border-transparent bg-secondary-500/20': !error,
            }
          ),
          'candidate-light': classNames(' rounded-lg border', {
            '!border-[#9585FA]': isFocused,
            '!border-error !bg-error-20%': error,
            'border-transparent  text-neutral-1000 !bg-neutral-0 !border-jb-neutral-500':
              !error,
          }),
          transparent: classNames('rounded-lg text-neutral-800 border', {
            '!border-primary-600': !isFocused,
            '!border-neutral-300': isFocused,
            'border-error bg-error-20%': error,
            'border-transparent bg-neutral-100': !error,
          }),
          transparentNeutralBorder: classNames(
            'rounded-lg text-neutral-900 border !p-1',
            {
              'border-error ': error,
              'border-neutral-500 bg-neutral-100': !error,
            }
          ),
        },
        sizeVariant: {
          xs: 'px-2 py-0 text-body-small font-normal',
          sm: '!px-2 !py-0 text-body-small font-normal',
          'candidate-base': 'px-2 py-1 text-body font-normal',
          base: 'p-2 text-body font-semibold',
        },
      },
      defaultVariants: {
        variant: 'primary',
        sizeVariant: 'base',
      },
    }
  );

type OptionStyleProps = VariantProps<ReturnType<typeof optionStyle>>;
const optionStyle = ({ isFocused }: { isFocused: boolean }) =>
  cva('my-1 px-3 py-2 text-neutral-200 ', {
    variants: {
      variant: {
        primary: classNames('rounded-lg', {
          'bg-primary-700': isFocused,
          'bg-primary-900': !isFocused,
        }),
        'candidate-primary': classNames('', {
          'bg-jb-neutral-300 !text-jb-neutral-1000': isFocused,
          'bg-transparent !text-jb-neutral-1000': !isFocused,
        }),
        'neutral-1000': classNames('rounded-lg', {
          'bg-neutral-800': isFocused,
          'bg-neutral-1000': !isFocused,
        }),
        gray: classNames('rounded-lg', {
          'bg-neutral-700': isFocused,
          'bg-neutral-900': !isFocused,
        }),
        grayWhiteText: classNames('rounded-lg', {
          'bg-neutral-700': isFocused,
          'bg-neutral-900': !isFocused,
        }),
        lightGray: classNames('rounded-lg', {
          'bg-neutral-300 !text-neutral-1000': isFocused,
          'bg-neutral-100 !text-neutral-1000': !isFocused,
        }),
        secondary: classNames('rounded-lg', {
          'bg-secondary-700': isFocused,
          'bg-secondary-900': !isFocused,
        }),
        light: classNames('', {
          'bg-primary-500 text-neutral-100': isFocused,
          'bg-neutral-0 !text-neutral-1000': !isFocused,
        }),
        'candidate-light': classNames('border-b-2', {
          'bg-black text-neutral-1000': isFocused,
          'bg-neutral-0 !text-neutral-1000': !isFocused,
        }),
        transparent: classNames('rounded-lg', {
          'bg-primary-500': !isFocused,
          'bg-primary-700': isFocused,
        }),
        transparentNeutralBorder: classNames('rounded-lg ', {
          'bg-neutral-100 text-neutral-900': !isFocused,
          'bg-neutral-300 text-neutral-900': isFocused,
        }),
      },
      sizeVariant: {
        xs: '!text-body-small font-normal',
        sm: '!my-0 !px-4 !text-body-small font-normal',
        'candidate-base': 'text-body font-normal',
        base: 'text-body font-semibold',
      },
    },
    defaultVariants: {
      variant: 'primary',
      sizeVariant: 'base',
    },
  });
type MenuStyleProps = VariantProps<typeof menuStyle>;
const menuStyle = cva('!z-20 !w-max min-w-full max-w-xs rounded-lg  p-2 ', {
  variants: {
    variant: {
      primary: 'bg-primary-900 text-white',
      'candidate-primary':
        'mt-2 border border-jb-primary-500 bg-jb-neutral-200 text-jb-neutral-1000',
      'neutral-1000': 'bg-neutral-1000 text-white',
      gray: 'bg-neutral-1000 text-white',
      grayWhiteText: 'bg-neutral-1000 text-white',
      lightGray: 'bg-neutral-100 text-neutral-1000',
      secondary: 'bg-secondary-900 text-white',
      light: 'mt-2 overflow-hidden bg-neutral-0 !p-0 text-neutral-1000',
      'candidate-light':
        'mt-2 overflow-hidden bg-neutral-0 !p-0 text-neutral-1000',
      transparent:
        'bg-neutral-100 text-neutral-800 !shadow-2xl shadow-neutral-800',
      transparentNeutralBorder: 'border border-neutral-500 bg-neutral-100',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});
type MultiValueStyleProps = VariantProps<typeof menuStyle>;

export const multiValueStyle = (isDisabled: boolean) =>
  cva('', {
    variants: {
      variant: {
        primary: classNames(
          'rounded-lg border-primary-500 bg-primary-700 text-white disabled:border-neutral-500 disabled:bg-neutral-700',
          {
            '!bg-neutral-800 !border-neutral-700': isDisabled,
          }
        ),
        'candidate-primary':
          'rounded-lg border-jb-neutral-1000 bg-jb-neutral-100 text-jb-neutral-1000',
        'neutral-1000': classNames(
          'rounded-lg border-neutral-600 bg-neutral-900 text-white disabled:border-neutral-500 disabled:bg-neutral-700',
          {
            '!bg-neutral-800 !border-neutral-700': isDisabled,
          }
        ),
        gray: 'rounded-lg border-neutral-500 bg-neutral-700 text-white',
        grayWhiteText:
          'rounded-lg border-neutral-500 bg-neutral-700 text-white',
        lightGray: 'rounded-lg border-0 bg-neutral-300 text-neutral-1000',
        secondary:
          'rounded-lg border-secondary-500 bg-secondary-700 text-white',
        light: 'rounded-lg border-neutral-1000 bg-neutral-0 text-neutral-1000',
        'candidate-light':
          'border-neutral-[#7C6D6D] rounded-lg bg-neutral-0 text-neutral-1000',
        transparent: 'rounded-lg border-primary-500 bg-primary-700 !text-white',
        transparentNeutralBorder: 'border-neutral-500 text-neutral-900',
      },
      sizeVariant: {
        xs: 'border-0 p-0',
        sm: 'border p-1',
        'candidate-base': 'border px-2 py-1',
        base: 'border-2 p-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      sizeVariant: 'base',
    },
  });

type SingleValueStyleProps = VariantProps<typeof singleValueStyle>;

const singleValueStyle = cva('', {
  variants: {
    variant: {
      primary: 'text-white',
      'candidate-primary': 'text-jb-neutral-1000',
      'neutral-1000': 'text-white',
      gray: 'text-primary-300',
      grayWhiteText: 'text-jb-body-medium text-white',
      lightGray: 'text-neutral-1000',
      secondary: 'text-white',
      light: 'text-neutral-1000',
      'candidate-light': 'text-neutral-1000',
      transparent: 'text-neutral-800',
      transparentNeutralBorder: 'text-neutral-900',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export interface SelectVariantProps
  extends MenuStyleProps,
    OptionStyleProps,
    ControlStyleProps,
    MultiValueStyleProps,
    SingleValueStyleProps {}

export type Props = SelectVariantProps & {
  error?: string;
  labelClassName?: string;
  label?: string;
  description?: string;
  labelVariant?: LabelStyles;
};

export const Select = <
  T extends object & { isFixed?: boolean },
  IsMulti extends boolean = false,
  IsCreatable extends boolean = false,
  IsAsync extends boolean = false,
  IsAsyncCreatable extends boolean = false
>({
  variant,
  sizeVariant,
  error,
  label,
  labelClassName,
  required,
  description,
  isCreatable,
  isAsync,
  isAsyncCreatable,
  labelVariant,
  ...props
}: SelectProps<T, IsMulti, IsCreatable, IsAsync, IsAsyncCreatable> & Props) => {
  const isSecondaryLabel = variant === 'light';
  return (
    <BaseSelect
      {...props}
      onChange={props.onChange}
      isCreatable={isCreatable as true | undefined}
      isAsync={isAsync}
      isAsyncCreatable={isAsyncCreatable}
      errorElement={<span className="text-error">{error}</span>}
      classNames={{
        ...props.classNames,
        control: (classNameProps) =>
          classNames(
            controlStyle({
              isDisabled: classNameProps.isDisabled,
              isFocused: classNameProps.isFocused,
              error: !!error,
            })({
              variant,
              sizeVariant,
            }),
            props.classNames?.control?.(classNameProps)
          ),
        option: (classNameProps) =>
          classNames(
            optionStyle({ isFocused: classNameProps.isFocused })({
              variant,
              sizeVariant,
            }),
            props.classNames?.option?.(classNameProps)
          ),
        menu: (classNameProps) =>
          classNames(
            menuStyle({ variant }),
            props.classNames?.menu?.(classNameProps)
          ),
        multiValue: (classNameProps) =>
          classNames(
            multiValueStyle(classNameProps.isDisabled)({
              variant,
              sizeVariant,
            }),
            props.classNames?.multiValue?.(classNameProps)
          ),
        singleValue: (classNameProps) =>
          classNames(
            singleValueStyle({ variant }),
            props.classNames?.singleValue?.(classNameProps)
          ),
      }}
      labelElement={
        <>
          {label && (
            <Label
              required={required}
              className={classNames(
                labelClassName,
                labelStyles({
                  ...(isSecondaryLabel && { variant: 'secondary' }),
                  ...labelVariant,
                })
              )}
            >
              {label}
            </Label>
          )}
        </>
      }
      description={
        <>
          {description && (
            <Text as="span" className="text-gray-300">
              {description}
            </Text>
          )}
        </>
      }
    />
  );
};
